import { PropsWithChildren, useState } from "react";

import { authStore } from "@/auth";
import { STATSIG_CLIENT_KEY } from "@/config";
import { StatsigUserSync } from "@/features/statsig/StatsigUserSync";
import { useExperimentExposureTracking } from "@/features/statsig/experiment-exposure";
import { registerStatsigAnalyticsClient } from "@/features/statsig/statsig-analytics-client";
import { createStatsigUser } from "@/features/statsig/utils/create-statsig-user";
import { getStatsigOptions } from "@/features/statsig/utils/get-statsig-options";
import {
  StatsigProvider,
  type StatsigUser,
  useClientAsyncInit,
  useClientBootstrapInit,
} from "@statsig/react-bindings";

let warned = false;

const consoleWarnOnce = (message: string) => {
  if (warned) {
    return;
  }

  warned = true;
  console.warn(message);
};

/**
 * Shape produced by `getStatsigProps` on the server. `userID` arrives as `null`
 * rather than `undefined` because Next refuses to serialise `undefined` in
 * `getServerSideProps` props.
 */
export type ExperimentBootstrapValues = {
  user: Omit<StatsigUser, "userID"> & { userID?: string | null };
  initializeValues: Record<string, unknown>;
};

type ExperimentsProviderProps = PropsWithChildren<{
  values?: ExperimentBootstrapValues;
}>;

export const ExperimentsProvider = ({
  children,
  values,
}: ExperimentsProviderProps) => {
  if (!STATSIG_CLIENT_KEY) {
    consoleWarnOnce(
      "STATSIG_CLIENT_KEY is not defined. Experiments will not be enabled."
    );
    return children;
  }

  return (
    <Experiments sdkKey={STATSIG_CLIENT_KEY} values={values}>
      {children}
    </Experiments>
  );
};

const Experiments = ({
  sdkKey,
  values,
  children,
}: ExperimentsProviderProps & { sdkKey: string }) => {
  // Frozen at mount on purpose. Bootstrapping only affects the first,
  // server-rendered paint; from then on it's a SPA and the client already holds
  // values. Re-deciding per navigation would swap the component underneath and
  // remount the entire app tree.
  const [bootstrapValues] = useState(() => values ?? null);

  if (bootstrapValues) {
    return (
      <BootstrappedExperiments sdkKey={sdkKey} values={bootstrapValues}>
        {children}
      </BootstrappedExperiments>
    );
  }

  return <NetworkExperiments sdkKey={sdkKey}>{children}</NetworkExperiments>;
};

/**
 * Initialises synchronously from values the server already evaluated, so the
 * server-rendered HTML carries the visitor's real variant. This is what keeps
 * A/B tests from shifting the layout after hydration.
 */
const BootstrappedExperiments = ({
  sdkKey,
  values,
  children,
}: PropsWithChildren<{
  sdkKey: string;
  values: ExperimentBootstrapValues;
}>) => {
  const client = useClientBootstrapInit(
    sdkKey,
    { ...values.user, userID: values.user.userID ?? undefined },
    JSON.stringify(values.initializeValues),
    getStatsigOptions(),
    // statsig-node emits the legacy payload format.
    true
  );

  useExperimentExposureTracking(client);
  registerStatsigAnalyticsClient(client);

  return (
    <StatsigProvider client={client}>
      <StatsigUserSync client={client} />
      {children}
    </StatsigProvider>
  );
};

/**
 * Fallback for pages that don't bootstrap: the client fetches values over the
 * network, so experiments read as their default until those land.
 */
const NetworkExperiments = ({
  sdkKey,
  children,
}: PropsWithChildren<{ sdkKey: string }>) => {
  // Non-reactive read, as it was before: subscribing here would remount the
  // whole tree whenever auth state settles. `StatsigUserSync` pushes the user
  // ID into Statsig once it resolves.
  const { userId, deviceId } = authStore.getState();

  const { client } = useClientAsyncInit(
    sdkKey,
    createStatsigUser({ userId: userId ?? undefined, deviceId }),
    getStatsigOptions()
  );

  useExperimentExposureTracking(client);
  registerStatsigAnalyticsClient(client);

  return (
    <StatsigProvider client={client}>
      <StatsigUserSync client={client} />
      {children}
    </StatsigProvider>
  );
};
